// Station health: alerts, telemetry and the needs-attention list (spec/10 §6.6, spec/13 §3.1-§3.3,
// spec/04 §3 `station_alert` / `station_telemetry`; F-STN-11, F-LCK-02, F-LCK-08, F-STN-15).
//
//   GET /v1/organizations/{o}/venues/{v}/stations/{stationId}/health      stations:read
//   GET /v1/organizations/{o}/venues/{v}/stations/{stationId}/telemetry   stations:read
//   GET /v1/organizations/{o}/venues/{v}/needs-attention                  floor:read
//   POST /v1/organizations/{o}/venues/{v}/stations/{stationId}/alerts/clear   stations:alerts_clear (2026-10-02)
//
// **Where the rows come from.** A station raises and clears alerts as the sequenced `station.alert` event
// (`realtime/station.ts`), which the api records once per `seq` into `station_alert` — one open row per
// station and kind, closed (`clearedAt`) by the station's own clear, so the console's badge disappears
// without a manual dismiss. The api raises one kind itself: `offline_in_session`, when a station drops its
// socket while a session runs on it (N-18). Telemetry is `station.status` (presence only, spec/09 §8)
// sampled by the gateway **at most once a minute per station** and written as one `station_telemetry` row —
// never a row per report, and the agent's 5-second health frames never leave the PC (spec/19 §7 rule 1).
//
// **How an alert goes (2026-10-02).** Each kind has a clear rule (`STATION_ALERT_CLEAR_RULE`): a condition clears when
// the station (or the api, for its own kinds) reports it gone; an event-like kind clears after a quiet period — the
// station's own, and the api's backstop `station.alertExpiryHours`. The station's `station.status` also carries the alerts
// it has open (`openAlerts`), so the api closes one the station no longer has and re-opens one staff cleared by hand while
// the station still reports it. Staff may clear or snooze alerts by hand (`stations:alerts_clear`), with a note, audited:
// a snoozed alert stays open but leaves the badge and "Needs attention" until the snooze ends.
//
// The spec's paths (`GET /v1/stations/{id}/health`, `…/telemetry`) are spelled here in the organisation-
// and venue-scoped form every other station route takes (spec/08 §1.2), so the permission check and the
// row-level scope have the venue they need.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { MemberIdSchema, OrganizationIdSchema, StationAlertIdSchema, StationCommandIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { StationCommandStatusSchema, StationCommandTypeSchema } from "../realtime/commands.js";
import { STATION_ALERT_CLEAR_RULES, StationAlertKindSchema, StationAlertSeveritySchema, StationModeSchema } from "../realtime/station.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { FLOOR_READ, STATIONS_ALERTS_CLEAR, STATIONS_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
/** How an alert clears by itself (`STATION_ALERT_CLEAR_RULE`): when its condition is gone, or after a quiet period. */
export const StationAlertClearRuleSchema = z.enum(STATION_ALERT_CLEAR_RULES).openapi('StationAlertClearRule', {
    description: '`condition`: clears when the station reports the condition gone; `quiet_period`: an event that clears after a quiet period with no repeat.',
});
/**
 * Why a cleared alert closed (2026-10-02): `resolved` — the station (or the api, for its own kinds) reported the
 * condition gone, or stopped reporting it open; `expired` — an event-like kind past `station.alertExpiryHours` with no
 * repeat; `staff` — cleared by hand.
 */
export const STATION_ALERT_CLEAR_REASONS = ['resolved', 'expired', 'staff'];
export const StationAlertClearReasonSchema = z.enum(STATION_ALERT_CLEAR_REASONS).openapi('StationAlertClearReason', {
    description: 'Why an alert closed: the condition went (`resolved`), it expired after a quiet period (`expired`), or staff cleared it (`staff`).',
});
/** One alert, open or cleared (spec/04 §3 `station_alert`). */
export const StationAlertSchema = z
    .object({
    id: StationAlertIdSchema,
    stationId: StationIdSchema,
    kind: StationAlertKindSchema,
    severity: StationAlertSeveritySchema,
    /** What the station saw, for staff. `null` when it said nothing more than the kind. */
    detail: z.string().nullable(),
    openedAt: IsoDateTimeSchema,
    /** When the condition cleared, or `null` while it is open. */
    clearedAt: IsoDateTimeSchema.nullable(),
    // 2026-10-02 (F-STN-11): how it clears and how it did. Each is optional rather than nullable — absent when it does
    // not apply — so the station's generated DTO gains no required nullable member.
    /** How this kind clears by itself. */
    clearRule: StationAlertClearRuleSchema.optional(),
    /** For an open `quiet_period` alert: when the api expires it if nothing repeats it (`station.alertExpiryHours`). */
    expiresAt: IsoDateTimeSchema.optional(),
    /** For a cleared alert: why it closed. */
    clearReason: StationAlertClearReasonSchema.optional(),
    /** For an alert staff cleared or snoozed: the member who did. */
    clearedByMemberId: MemberIdSchema.optional(),
    /** The note staff left when they cleared or snoozed it. */
    clearNote: z.string().max(500).optional(),
    /** For an open alert staff snoozed: until when it stays off the badge and "Needs attention". */
    snoozedUntil: IsoDateTimeSchema.optional(),
})
    .openapi('StationAlert', { description: 'A health or security alert a station raised, open or cleared (spec/10 §6.6).' });
/** One telemetry sample: a `station.status` the gateway kept (at most one a minute). */
export const StationTelemetrySampleSchema = z
    .object({
    at: IsoDateTimeSchema,
    mode: StationModeSchema,
    cpuPct: z.int().min(0).max(100),
    ramPct: z.int().min(0).max(100),
    /** `null` where the GPU does not report it. */
    gpuTempC: z.int().nullable(),
    diskFreeGb: z.int().min(0),
    uptimeSeconds: z.int().min(0),
    idleSeconds: z.int().min(0),
    /** The foreground executable, or `null` on the lock screen or an empty desktop. */
    foregroundExe: z.string().nullable(),
})
    .openapi('StationTelemetrySample', { description: 'One sampled `station.status` (spec/04 §3 `station_telemetry`).' });
/** A station is `unhealthy` while any alert is open and not snoozed; `unknown` before it has ever reported. */
export const STATION_HEALTH_STATUSES = ['healthy', 'unhealthy', 'unknown'];
export const StationHealthStatusSchema = z.enum(STATION_HEALTH_STATUSES).openapi('StationHealthStatus', {
    description: '`unhealthy` while an alert is open (and not snoozed); `unknown` before the station has reported anything.',
});
/**
 * Whether the station enforces the settings the venue has now (spec/10 §6.2, spec/13 §3.3): the policy bundle
 * version the api would build for it now, against the one it last reported enforcing (`station.status`).
 */
export const StationPolicyStatusSchema = z
    .object({
    /** The bundle version the api builds for this station now — a content hash of its resolved settings. */
    currentVersion: z.string().min(1).max(64),
    /** The version the station last reported enforcing, or `null` when it has not reported one. */
    appliedVersion: z.string().min(1).max(64).nullable(),
    /** When the station reported `appliedVersion`, or `null`. */
    reportedAt: IsoDateTimeSchema.nullable(),
    /** `true` when the two match; `false` while a change is on its way (or failed to apply); `null` when the station has not reported. */
    upToDate: z.boolean().nullable(),
})
    .openapi('StationPolicyStatus', {
    description: 'The station’s policy bundle: the version the api builds now against the one the station enforces (F-STN-09).',
});
export const StationHealthSchema = z
    .object({
    stationId: StationIdSchema,
    status: StationHealthStatusSchema,
    /** Every open alert, oldest first. */
    open: z.array(StationAlertSchema),
    /** The last alerts, open and cleared, newest first (at most 20) — the drawer's history. */
    recent: z.array(StationAlertSchema),
    /** The latest telemetry sample, or `null` when none has been kept. */
    latest: StationTelemetrySampleSchema.nullable(),
    /** The policy bundle the station enforces against the one it should (F-STN-09). */
    policy: StationPolicyStatusSchema,
})
    .openapi('StationHealth', { description: 'A station’s health: its open alerts, recent history and latest sample (F-STN-11).' });
/** The most samples one telemetry read returns: a day at one a minute. */
export const MAX_TELEMETRY_SAMPLES = 1440;
export const StationTelemetryQuerySchema = z.object({
    /** How far back, in hours (default 24, at most 72). */
    hours: z.coerce.number().int().min(1).max(72).optional(),
});
export const StationTelemetrySchema = z
    .object({
    stationId: StationIdSchema,
    /** Oldest first. */
    samples: z.array(StationTelemetrySampleSchema).max(MAX_TELEMETRY_SAMPLES),
})
    .openapi('StationTelemetry', { description: 'A station’s telemetry samples over a window, oldest first (F-STN-11).' });
/** A station command that did not happen: `failed` (the station said why) or `expired` (it never answered). */
export const StationCommandIssueSchema = z
    .object({
    id: StationCommandIdSchema,
    stationId: StationIdSchema,
    type: StationCommandTypeSchema,
    status: StationCommandStatusSchema,
    /** The station's error code for a failed command; `null` for an expired one. */
    errorCode: z.string().nullable(),
    /** The station's error message, when it gave one. */
    errorMessage: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    /** When it failed or expired. */
    completedAt: IsoDateTimeSchema,
})
    .openapi('StationCommandIssue', { description: 'A station command that failed or expired, with its error (spec/13 §3.2).' });
/** How far back "What needs you" looks for failed and expired commands. */
export const NEEDS_ATTENTION_COMMAND_HOURS = 12;
export const NeedsAttentionSchema = z
    .object({
    /** Every open station alert at the venue, critical first, then oldest first. */
    alerts: z.array(StationAlertSchema),
    /** Commands that failed or expired in the last 12 hours, newest first — only the latest per station and type. */
    commands: z.array(StationCommandIssueSchema),
})
    .openapi('NeedsAttention', {
    description: 'The "Needs attention" section of "What needs you": open station alerts and failed or expired commands (spec/13 §3.2).',
});
/** The most hours one snooze lasts. */
export const MAX_ALERT_SNOOZE_HOURS = 72;
/**
 * Clear a station's alerts by hand (2026-10-02, F-STN-11): the alerts named in `alertIds`, or every open alert on the
 * station when it is absent. With `snoozeHours` they are snoozed instead — left open but off the badge and "Needs
 * attention" until then.
 */
export const ClearStationAlertsRequestSchema = z
    .object({
    /** The open alerts to clear; absent clears every open alert on the station. */
    alertIds: z.array(StationAlertIdSchema).min(1).max(50).optional(),
    /** Why — "replaced the cable", "known, waiting for a part". Recorded on the alert and in the audit log. */
    note: z.string().trim().min(1).max(500).optional(),
    /** Snooze instead of clear: hide them for this many hours (1–72). */
    snoozeHours: z.int().min(1).max(MAX_ALERT_SNOOZE_HOURS).optional(),
})
    .openapi('ClearStationAlertsRequest', {
    description: 'Clear (or, with `snoozeHours`, snooze) one or all of a station’s open alerts, with an optional note.',
});
export const StationAlertsClearedSchema = z
    .object({
    /** The alerts this call cleared or snoozed, as they are now. An alert already closed is left out. */
    alerts: z.array(StationAlertSchema),
})
    .openapi('StationAlertsCleared', { description: 'The alerts a clear or snooze changed.' });
const StationHealthParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, stationId: StationIdSchema })
    .openapi('StationHealthParams');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const stationNotFound = problemResponse('No such station at this venue.', 'not-found');
export const getStationHealthRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/health',
    operationId: 'getStationHealth',
    tags: ['stations'],
    summary: 'A station’s health',
    description: 'The station’s open alerts, its recent alert history, its latest telemetry sample, and whether it enforces the ' +
        'settings the venue has now (the policy bundle version it reported against the one the api builds for it now, ' +
        'F-STN-09) — the station drawer’s health section (F-STN-11, spec/13 §3.3). Requires `stations:read`.',
    [PERMISSION_EXTENSION]: STATIONS_READ,
    request: { params: StationHealthParamsSchema },
    responses: {
        200: jsonResponse(StationHealthSchema, 'The station’s health.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getStationTelemetryRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/telemetry',
    operationId: 'getStationTelemetry',
    tags: ['stations'],
    summary: 'A station’s telemetry',
    description: 'The station’s sampled `station.status` over the last `hours` (default 24): mode, CPU, RAM, GPU temperature and ' +
        'free disk, at most one sample a minute (F-STN-11). Retained 90 days. Requires `stations:read`.',
    [PERMISSION_EXTENSION]: STATIONS_READ,
    request: { params: StationHealthParamsSchema, query: StationTelemetryQuerySchema },
    responses: {
        200: jsonResponse(StationTelemetrySchema, 'The station’s samples, oldest first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getNeedsAttentionRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/needs-attention',
    operationId: 'getNeedsAttention',
    tags: ['stations'],
    summary: 'What needs attention at a venue',
    description: 'The "Needs attention" section of the floor’s "What needs you" panel: every open station alert and the station ' +
        'commands that failed or expired in the last 12 hours (spec/13 §3.2, F-STN-15). Requires `floor:read`.',
    [PERMISSION_EXTENSION]: FLOOR_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(NeedsAttentionSchema, 'What needs attention.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such venue in this organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const clearStationAlertsRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/alerts/clear',
    operationId: 'clearStationAlerts',
    tags: ['stations'],
    summary: 'Clear or snooze a station’s alerts',
    description: 'Clear one, several or all of a station’s open health alerts by hand, with an optional note (F-STN-11, spec/13 §3.3). ' +
        'A condition the station still reports opens again as a new alert, so a clear never silences a real problem. With ' +
        '`snoozeHours` the alerts are snoozed instead: left open, but off the floor’s badge and "Needs attention" until then. ' +
        'Each change publishes `alerts.cleared` on the venue floor. Requires `stations:alerts_clear`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_ALERTS_CLEAR,
    request: {
        params: StationHealthParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: ClearStationAlertsRequestSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationAlertsClearedSchema, 'The alerts cleared or snoozed.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-health.js.map